class CollectionTabs extends HTMLElement {
  constructor() {
    super();
    this.tabs = this.querySelectorAll('.collection-tabs__button');
    this.currentCollectionHandle = this.getCurrentCollectionHandle();
    this.init();
  }

  init() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.handleTabClick.bind(this));
    });
  }

  getCurrentCollectionHandle() {
    // Get collection handle from URL path
    const pathMatch = window.location.pathname.match(/\/collections\/([^\/]+)/);
    if (pathMatch) {
      return pathMatch[1];
    }
    // Check if we're on /collections/all or /products
    if (window.location.pathname === '/collections/all' || window.location.pathname === '/products') {
      return 'all';
    }
    return 'all';
  }

  async handleTabClick(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const collectionHandle = button.dataset.collectionHandle;
    const collectionUrl = button.dataset.collectionUrl;

    // Don't reload if clicking the same tab
    if (collectionHandle === this.currentCollectionHandle) {
      return;
    }

    // Update active state
    this.updateActiveTab(button);

    // Get current filters and sorting
    const currentFilters = this.getCurrentFilters();
    const currentSort = this.getCurrentSort();

    // Build new URL with filters and sort preserved
    const newUrl = this.buildUrl(collectionUrl, currentFilters, currentSort);

    // Update URL without reload
    window.history.pushState({ collectionHandle }, '', newUrl);
    this.currentCollectionHandle = collectionHandle;

    // Reload all sections via AJAX
    await this.reloadAllSections(newUrl);
  }

  updateActiveTab(activeButton) {
    this.tabs.forEach((tab) => {
      tab.classList.remove('collection-tabs__button--active');
      tab.setAttribute('aria-selected', 'false');
    });
    activeButton.classList.add('collection-tabs__button--active');
    activeButton.setAttribute('aria-selected', 'true');
  }

  getCurrentFilters() {
    const urlParams = new URLSearchParams(window.location.search);
    const filters = {};
    // Get all filter parameters (excluding sort_by and page)
    urlParams.forEach((value, key) => {
      if (key !== 'sort_by' && key !== 'page' && key !== 'section_id') {
        filters[key] = value;
      }
    });
    return filters;
  }

  getCurrentSort() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('sort_by') || '';
  }

  buildUrl(baseUrl, filters, sort) {
    // Handle relative URLs
    let url;
    if (baseUrl.startsWith('http')) {
      url = new URL(baseUrl);
    } else {
      url = new URL(baseUrl, window.location.origin);
    }

    // Add filters
    Object.keys(filters).forEach((key) => {
      if (key !== 'collection') {
        url.searchParams.set(key, filters[key]);
      }
    });

    // Add sort
    if (sort) {
      url.searchParams.set('sort_by', sort);
    }

    return url.toString();
  }

  async reloadAllSections(url) {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return;

    const productGridSectionId = productGrid.dataset.id;
    if (!productGridSectionId) return;

    const urlObj = new URL(url);
    
    // Show loading states
    const productGridContainer = document.getElementById('ProductGridContainer');
    const loadingOverlay = productGridContainer?.querySelector('.loading-overlay');
    
    if (productGridContainer) {
      const collection = productGridContainer.querySelector('.collection');
      if (collection) {
        collection.classList.add('loading');
      }
      if (loadingOverlay) {
        loadingOverlay.classList.remove('hidden');
      }
    }

    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    if (countContainer) countContainer.classList.add('loading');
    if (countContainerDesktop) countContainerDesktop.classList.add('loading');

    try {
      const parser = new DOMParser();

      // 1. Fetch and update the main product grid section
      const productGridParams = new URLSearchParams(urlObj.search);
      productGridParams.set('section_id', productGridSectionId);
      const productGridUrl = `${urlObj.pathname}?${productGridParams.toString()}`;
      
      const productGridResponse = await fetch(productGridUrl);
      const productGridHtml = await productGridResponse.text();
      const productGridDoc = parser.parseFromString(productGridHtml, 'text/html');

      // Update title
      const newTitle = productGridDoc.querySelector('.collection_title');
      if (newTitle) {
        const currentTitle = document.querySelector('.collection_title');
        if (currentTitle) {
          currentTitle.innerText = newTitle.innerText;
        }
      }

      // Update description
      const newDescription = productGridDoc.querySelector('.collection_description');
      if (newDescription) {
        const currentDescription = document.querySelector('.collection_description');
        if (currentDescription) {
          currentDescription.innerHTML = newDescription.innerHTML;
        }
      }

      // Update product grid container
      const newProductGridContainer = productGridDoc.getElementById('ProductGridContainer');
      if (newProductGridContainer && productGridContainer) {
        productGridContainer.innerHTML = newProductGridContainer.innerHTML;
      }

      // Update product count
      const newProductCount = productGridDoc.getElementById('ProductCount');
      const currentProductCount = document.getElementById('ProductCount');
      if (newProductCount && currentProductCount) {
        currentProductCount.innerHTML = newProductCount.innerHTML;
        currentProductCount.classList.remove('loading');
      }

      const newProductCountDesktop = productGridDoc.getElementById('ProductCountDesktop');
      const currentProductCountDesktop = document.getElementById('ProductCountDesktop');
      if (newProductCountDesktop && currentProductCountDesktop) {
        currentProductCountDesktop.innerHTML = newProductCountDesktop.innerHTML;
        currentProductCountDesktop.classList.remove('loading');
      }

      // Update filters
      const newFilters = productGridDoc.getElementById('main-collection-filters');
      const currentFilters = document.getElementById('main-collection-filters');
      if (newFilters && currentFilters) {
        currentFilters.innerHTML = newFilters.innerHTML;
      }

      // 2. Fetch and update the image-text-blocks section
      const imageTextBlocksWrapper = document.getElementById('collection-img-txt-blocks-wrapper');
      if (imageTextBlocksWrapper) {
        const imageTextSectionId = imageTextBlocksWrapper.dataset.id;
        if (imageTextSectionId) {
          const imageTextParams = new URLSearchParams();
          imageTextParams.set('section_id', imageTextSectionId);
          const imageTextUrl = `${urlObj.pathname}?${imageTextParams.toString()}`;
          
          try {
            const imageTextResponse = await fetch(imageTextUrl);
            const imageTextHtml = await imageTextResponse.text();
            const imageTextDoc = parser.parseFromString(imageTextHtml, 'text/html');
            
            const newImageTextBlocks = imageTextDoc.getElementById('collection-img-txt-blocks-wrapper');
            
            if (newImageTextBlocks) {
              imageTextBlocksWrapper.innerHTML = newImageTextBlocks.innerHTML;
            }
          } catch (error) {
            console.error('Error updating image-text blocks:', error);
          }
        }
      }

      // 3. Fetch and update the FAQs section
      const faqWrapper = document.getElementById('coll-faq-wrapper');
      if (faqWrapper) {
        const faqSectionId = faqWrapper.dataset.id;
        if (faqSectionId) {
          const faqParams = new URLSearchParams();
          faqParams.set('section_id', faqSectionId);
          const faqUrl = `${urlObj.pathname}?${faqParams.toString()}`;
          
          try {
            const faqResponse = await fetch(faqUrl);
            const faqHtml = await faqResponse.text();
            const faqDoc = parser.parseFromString(faqHtml, 'text/html');
            
            const newFAQWrapper = faqDoc.getElementById('coll-faq-wrapper');
            
            if (newFAQWrapper) {
              faqWrapper.innerHTML = newFAQWrapper.innerHTML;

              // Reinitialize accordion
              new CollectionFAQAccordion(faqWrapper);
            }

          } catch (error) {
            console.error('Error updating FAQs:', error);
          }
        }
      }

      // Remove loading states
      if (productGridContainer) {
        const collection = productGridContainer.querySelector('.collection');
        if (collection) {
          collection.classList.remove('loading');
        }
        if (loadingOverlay) {
          loadingOverlay.classList.add('hidden');
        }
      }

      loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

      // Reinitialize scroll animations if available
      if (typeof initializeScrollAnimationTrigger === 'function') {
        initializeScrollAnimationTrigger(document.body);
      }

      // Dispatch custom event for other scripts
      document.dispatchEvent(new CustomEvent('collection:updated', {
        detail: { collectionHandle: this.currentCollectionHandle }
      }));

    } catch (error) {
      console.error('Error reloading sections:', error);
      
      // Remove loading states on error
      if (productGridContainer) {
        const collection = productGridContainer.querySelector('.collection');
        if (collection) {
          collection.classList.remove('loading');
        }
        if (loadingOverlay) {
          loadingOverlay.classList.add('hidden');
        }
      }
      
      loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
      if (countContainer) countContainer.classList.remove('loading');
      if (countContainerDesktop) countContainerDesktop.classList.remove('loading');
    }
  }

  // Handle browser back/forward buttons
  handlePopState(event) {
    const collectionHandle = this.getCurrentCollectionHandle();

    // Find matching tab
    const matchingTab = Array.from(this.tabs).find(
      (tab) => tab.dataset.collectionHandle === collectionHandle
    );

    if (matchingTab && collectionHandle !== this.currentCollectionHandle) {
      this.updateActiveTab(matchingTab);
      this.currentCollectionHandle = collectionHandle;
      this.reloadAllSections(window.location.href);
    }
  }
}

// Initialize on page load
if (!customElements.get('collection-tabs')) {
  customElements.define('collection-tabs', CollectionTabs);
}

// Handle browser navigation
document.addEventListener('DOMContentLoaded', () => {
  window.addEventListener('popstate', (event) => {
    const tabsElement = document.querySelector('collection-tabs');
    if (tabsElement) {
      tabsElement.handlePopState(event);
    }
  });
});
